import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import EditorialButton from "@/components/landing/EditorialButton";
import { STRIPE_LINKS, EVENT, IMAGES } from "@/lib/landingConfig";
import { useLandingImages } from "@/lib/ImagesContext";
import { useReservation } from "@/components/landing/ReservationModal";
import { useCountdown } from "@/components/landing/Countdown";

/**
 * Hero — the signature on-load moment of the site.
 * The masthead reveals line by line through overflow masks, the background
 * photograph settles from a slight zoom and then drifts with a slow
 * parallax on scroll. All copy, CTAs and data-testids are unchanged.
 */

const EASE = [0.22, 1, 0.36, 1];

const lineReveal = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, ease: EASE, delay: 0.35 + i * 0.16 },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 26 },
  show: (delay) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: EASE, delay },
  }),
};

const Hero = () => {
  const { bySlot } = useLandingImages();
  const { open: openReservation } = useReservation();
  const { days } = useCountdown();
  const heroImg = bySlot["hero"] || IMAGES.heroBg;

  const sectionRef = React.useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const markerY = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <section
      ref={sectionRef}
      id="hero"
      data-testid="hero-section"
      className="relative overflow-hidden bg-[#F4EFE7]"
    >
      {/* Background image with jade overlay + scroll parallax */}
      <motion.div
        className="absolute -inset-y-[12%] inset-x-0"
        style={{ y: bgY }}
        aria-hidden="true"
      >
        <motion.img
          src={heroImg}
          alt=""
          aria-hidden="true"
          className="h-full w-full object-cover opacity-45"
          initial={{ scale: 1.08 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease: EASE }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#F4EFE7]/70 via-[#F4EFE7]/60 to-[#F4EFE7]" />
        <div className="absolute inset-0 bg-[#63846F]/12" />
      </motion.div>

      {/* Top bar — minimal editorial nav */}
      <motion.div
        className="relative z-10 border-b border-[#3F403A]/10"
        initial={{ opacity: 0, y: -14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-24 md:h-32">
          <a
            href="#hero"
            data-testid="nav-logo"
            aria-label="Lidera con Voz Propia"
            className="block -ml-1 md:-ml-2"
            style={{
              width: "clamp(220px, 28vw, 380px)",
              height: "clamp(64px, 8vw, 108px)",
              backgroundImage: "url('/logo-lidera.png')",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "left center",
              backgroundSize: "contain",
            }}
          />
          {/* Screen-reader text is provided via aria-label on the anchor above */}
          <nav className="hidden md:flex items-center gap-8 overline text-[#3F403A]/80">
            <a href="#experiencia" className="nav-link hover:text-[#63846F] transition-colors" data-testid="nav-experiencia">La jornada</a>
            <a href="#ponentes" className="nav-link hover:text-[#63846F] transition-colors" data-testid="nav-ponentes">Ponentes</a>
            <a href="#inscripcion" className="nav-link hover:text-[#63846F] transition-colors" data-testid="nav-inscripcion">Inscripción</a>
            <a href="#faq" className="nav-link hover:text-[#63846F] transition-colors" data-testid="nav-faq">FAQ</a>
          </nav>
          <a
            href="#inscripcion"
            data-testid="nav-cta"
            className="hidden md:inline-flex items-center gap-2 border border-[#63846F] text-[#63846F] hover:bg-[#63846F] hover:text-[#FAF8F3] transition-colors px-5 py-2.5 overline"
          >
            Reservar plaza
          </a>
        </div>
      </motion.div>

      {/* Main hero content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-24 md:pt-28 md:pb-40 lg:pt-32 lg:pb-48">
        <div className="grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 lg:col-span-9">
            <motion.p
              className="overline text-[#63846F] mb-6 md:mb-8"
              data-testid="hero-eyebrow"
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={0.2}
            >
              {EVENT.editionLabel}
            </motion.p>

            <h1
              className="font-quiche text-[#3F403A] leading-[0.95] tracking-tight text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-[128px]"
              data-testid="hero-title"
            >
              <span className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
                <motion.span
                  className="block"
                  variants={lineReveal}
                  initial="hidden"
                  animate="show"
                  custom={0}
                >
                  Lidera con Voz
                </motion.span>
              </span>
              <span className="block overflow-hidden pb-[0.14em] -mb-[0.14em]">
                <motion.span
                  className="block"
                  variants={lineReveal}
                  initial="hidden"
                  animate="show"
                  custom={1}
                >
                  <strong className="quiche-bold font-extrabold text-[#63846F]">Propia</strong>
                </motion.span>
              </span>
            </h1>

            <motion.div
              className="mt-10 md:mt-14 flex flex-wrap items-center gap-x-6 gap-y-3"
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={0.95}
            >
              <span className="h-px w-12 bg-[#3F403A]/30" />
              <p
                className="overline text-[#3F403A]"
                data-testid="hero-date-city"
              >
                {EVENT.date} · {EVENT.city}
              </p>
              <span
                className="overline text-[#B77A4B]"
                data-testid="hero-countdown"
              >
                {days > 0 ? `Faltan ${days} días` : "La jornada está en marcha"}
              </span>
            </motion.div>

            <motion.p
              className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-[#3F403A]/85 font-sans"
              data-testid="hero-subtitle"
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={1.05}
            >
              Una jornada presencial de liderazgo, comunicación, estrategia y
              relaciones de alto valor para empresarias, CEOs y directivas que
              quieren crecer con criterio, visibilidad y voz propia.
            </motion.p>

            <motion.p
              className="mt-6 max-w-2xl text-sm md:text-base leading-relaxed text-[#3F403A]/70 font-sans italic"
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={1.15}
            >
              Un año después, Lidera con Voz Propia vuelve con una edición
              especial para mujeres que no quieren crecer a cualquier precio,
              sino liderar con dirección, influencia y coherencia.
            </motion.p>

            <motion.div
              className="mt-10 md:mt-12 flex flex-col sm:flex-row gap-4 sm:gap-5"
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={1.3}
            >
              {EVENT.generalBatchClosed ? (
                <a
                  href="#inscripcion"
                  className="inline-flex items-center justify-center gap-3 rounded-none border border-[#63846F] bg-transparent text-[#63846F] hover:bg-[#63846F] hover:text-[#FAF8F3] transition-colors px-8 py-4 md:px-10 md:py-5 uppercase tracking-[0.22em] text-xs md:text-[13px] font-medium"
                  data-testid="hero-cta-general-waitlist"
                >
                  Avísame del último lote · Pase General <span aria-hidden>→</span>
                </a>
              ) : (
                <EditorialButton
                  as="button"
                  type="button"
                  onClick={() => openReservation(STRIPE_LINKS.general, "Pase General")}
                  variant="jade"
                  data-testid="hero-cta-general"
                >
                  Reservar Pase General
                </EditorialButton>
              )}
              {EVENT.vipSoldOut ? (
                <a
                  href="#inscripcion"
                  className="inline-flex items-center justify-center gap-3 rounded-none border border-[#3F403A]/30 bg-transparent text-[#3F403A]/70 hover:border-[#B77A4B] hover:text-[#B77A4B] transition-colors px-8 py-4 md:px-10 md:py-5 uppercase tracking-[0.22em] text-xs md:text-[13px] font-medium"
                  data-testid="hero-cta-vip-waitlist"
                >
                  Lista de espera · Experiencia VIP <span aria-hidden>→</span>
                </a>
              ) : (
                <EditorialButton
                  as="button"
                  type="button"
                  onClick={() => openReservation(STRIPE_LINKS.vip, "Experiencia VIP")}
                  variant="outline"
                  data-testid="hero-cta-vip"
                >
                  Reservar Experiencia VIP
                </EditorialButton>
              )}
            </motion.div>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={1.45}
            >
              {(EVENT.generalBatchClosed || EVENT.vipSoldOut) ? (
                <div
                  className="mt-8 md:mt-10 max-w-2xl border-l-2 border-[#B77A4B]/70 pl-5 py-1"
                  data-testid="hero-status-note"
                >
                  {EVENT.generalBatchClosed && EVENT.vipSoldOut ? (
                    <p className="text-sm text-[#3F403A]/85 font-sans leading-relaxed">
                      <span className="text-[#B77A4B] font-medium">
                        Reservas temporalmente cerradas.
                      </span>{" "}
                      Se ha cerrado el segundo lote del Pase General con
                      condiciones especiales y la Experiencia VIP ha completado
                      aforo. En unas semanas se anunciará el último lote de plazas
                      disponibles — únete a la lista de espera y te avisaremos.
                    </p>
                  ) : EVENT.vipSoldOut ? (
                    <p className="text-sm text-[#3F403A]/85 font-sans leading-relaxed">
                      <span className="text-[#B77A4B] font-medium">
                        Últimas plazas del Pase General.
                      </span>{" "}
                      La Experiencia VIP ha completado aforo para esta edición.
                      Reserva tu Pase General mientras queden plazas disponibles.
                    </p>
                  ) : (
                    <p className="text-sm text-[#3F403A]/85 font-sans leading-relaxed">
                      <span className="text-[#B77A4B] font-medium">
                        Reservas del Pase General temporalmente cerradas.
                      </span>{" "}
                      En unas semanas se anunciará el último lote de plazas — únete
                      a la lista de espera y te avisaremos.
                    </p>
                  )}
                </div>
              ) : EVENT.code || EVENT.windowStart ? (
                <div
                  className="mt-8 md:mt-10 max-w-xl border-l-2 border-[#B77A4B]/70 pl-5 py-1"
                  data-testid="hero-code-note"
                >
                  {EVENT.windowStart && EVENT.windowEnd && (
                    <p className="text-sm text-[#3F403A]/80 font-sans">
                      Nuevo lote disponible del{" "}
                      <span className="text-[#B77A4B] font-medium">
                        {EVENT.windowStart} al {EVENT.windowEnd}
                      </span>
                      .
                    </p>
                  )}
                  {EVENT.code && (
                    <p className="mt-1 text-sm text-[#3F403A]/80 font-sans">
                      Código especial:{" "}
                      <span className="tracking-[0.2em] font-medium text-[#B77A4B]">
                        {EVENT.code}
                      </span>
                    </p>
                  )}
                </div>
              ) : null}
            </motion.div>
          </div>

          {/* Editorial vertical marker */}
          <div className="hidden lg:flex col-span-3 items-end justify-end">
            <motion.div
              className="text-right"
              style={{ y: markerY }}
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={1.6}
            >
              <p className="overline text-[#3F403A]/60">Nº 04</p>
              <p className="mt-4 font-serif text-3xl text-[#63846F] italic leading-tight">
                Edición<br />Aniversario
              </p>
              <div className="mt-6 ml-auto h-16 w-px bg-[#3F403A]/25" />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
